import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { ahuBoardApi, useGetAhuUnitQuery, usePatchAhuTunnelMutation } from 'app/services/ahuBoardApi';
import { useMoveAhuSectionMutation } from 'app/services/ahuBoardFormsApi';
import { selectSection } from 'app/slices/ahuBoardSlice';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { airflowFlipPatch } from '../hybridXOffset';
import { refetchHybridLugPlacements, reorderHybridTunnels } from '../../boardActions';
import {
  compareTunnelAddress,
  formatRowLevel,
  getVisibleTunnelIndexes,
  isHybridAhu,
} from '../../addressing';
import { formatDecimal } from '../../forms/formUtils';
import { validateAll3D, validateTunnel } from '../../validation/validationController';
import { analyticsComponentName, track } from 'app/analytics';

const MAX_VISIBLE_TUNNEL_INDEXES = 5;

/**
 * Phase 6e1 — Configure Tunnel window (§8.2, tunnels.section.js +
 * tunnels.section.tmpl + sections.order.list.tmpl, markup faithful): tunnel
 * pills, per-tunnel Airflow Direction toggles (the PATCH wire exists from 6d
 * — patchAhuTunnel with `{wait:true}` semantics), drag-reorder rows
 * ("Name : tag | length" + grip) and an optimistic renumber.
 *
 * **The window means two different things, and which one is on screen depends
 * on the AHU** (requested 2026-10-20):
 *
 *  - **Standard AHU — unchanged.** One tab per tunnel, the rows are that
 *    tunnel's SECTIONS in `order_rate` order, a drop is
 *    `section.move({next_to})` + optimistic renumber + `sections:reordered`,
 *    and the Airflow Direction toggle is there.
 *  - **Hybrid AHU.** One tab per `row:level` CELL (`1:1`, `1:2`, `2:1`), the
 *    rows are every tunnel of that cell in `position` order, and a drop sends
 *    the cell's new tunnel order to `reorder_tunnels` in one request. No
 *    Airflow Direction here: on a hybrid unit a tunnel is a single run of its
 *    own and the direction is already on the canvas arrow and in the Data Bar.
 *
 * A hybrid tunnel almost always holds exactly one section, so the rows read
 * the same as they always did; a tunnel that holds more lists all of its
 * sections in the one row and moves as a unit, because what is being
 * reordered is the tunnel.
 *
 * Pills/rows follow external section creation/selection
 * (`storage:section.model:create` / `select:section` — store-driven here).
 *
 * Row reordering is a hand-rolled pointer drag rather than HTML5 drag-and-drop,
 * matching `jquery/draggable.js` (`$.fn.draggables`, bound by tunnels.section.js
 * on `.drag`): the browser's native drag paints a free-floating ghost that
 * follows the cursor in both axes and escapes the panel, which dev never did.
 * Here the row is lifted out of flow and moved on Y only, clamped to the list's
 * visible box, with a drop slot opening at the landing position.
 */

/**
 * How long the rows have to sit still before a hybrid reorder is sent
 * (requested 2026-10-21). Every drop inside the window replaces the pending
 * order rather than queueing a request of its own, and starting another drag
 * restarts the clock — so three drags in a row are ONE request carrying the
 * final order, not three.
 */
const REORDER_DEBOUNCE_MS = 2000;

/** Pointer travel before a press turns into a drag (below this it is a click). */
const DRAG_THRESHOLD_PX = 4;
/** Distance from the list edge that starts an auto-scroll, and its step. */
const EDGE_SCROLL_ZONE_PX = 18;
const EDGE_SCROLL_STEP_PX = 15;
/**
 * How much taller than the row the drop slot is, per side. The slot paints
 * nothing — it is plain empty space — and the lifted row is exactly row-sized
 * and sits on it, so without this band there would be no visible gap at all.
 */
const DROP_SLOT_PAD_PX = 5;

interface ConfigureTunnelProps {
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
}

/** Live drag, mirrored into state so the list can render the slot. */
interface DragState {
  id: number;
  fromIndex: number;
  /** Index the row will land on, in the list as it will read after the drop. */
  toIndex: number;
  /** `top` of the floating row inside the UL's content box. */
  top: number;
  height: number;
}

/** Row geometry captured on press, before the list is disturbed. */
interface DragGeometry {
  rows: { top: number; height: number }[];
  fromIndex: number;
  startY: number;
  id: number;
  /** The list's visible height at press. Read live it would grow by the slot's
   *  extra band and feed the clamp back into itself. */
  viewport: number;
}

/** One tab: a single tunnel on a standard AHU, a `row:level` cell on a hybrid. */
interface TunnelTab {
  key: string;
  label: string;
  tunnels: IAhuBoardTunnel[];
}

/** One draggable row: a section on a standard AHU, a tunnel on a hybrid one. */
interface ListRow {
  /** What the drop reorders — a section id, or a tunnel id on a hybrid AHU. */
  id: number;
  tunnelId: number;
  /** "Name : tag", joined when a hybrid tunnel holds more than one section. */
  label: string;
  length: number;
  /** The section a click selects and a hover highlights; null on an empty tunnel. */
  sectionId: number | null;
  /** The section's type, for the `section_moved` analytics event. */
  type?: string | null;
}

const sectionLabel = (section: IAhuBoardSection) =>
  `${String(section.name ?? '')}${section.tag ? ` : ${section.tag}` : ''}`;

const toNumber = (value: number | string | null | undefined) => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed as number) ? (parsed as number) : 0;
};

/**
 * The tunnels holding the SECOND half of a two-tunnel component (Heat Wheel,
 * Plate Heatex) — the sections that carry `sitting_on_id`. Their `x_offset`
 * is derived from the first half (`dual_tunnel_x`), so the server refuses to
 * reorder a cell containing one: whatever a drag wrote would be undone by the
 * next save. Shown as un-draggable rather than left to fail on the drop.
 *
 * The FIRST half is deliberately not in here. It is an ordinary tunnel whose
 * X-Offset the user edits directly, and moving it is what moves the other
 * half — the server re-derives the pair after the reorder. Including it froze
 * a whole 7-tunnel row over one wheel (AHU 3471, reported 2026-10-20).
 */
const dualSecondaryTunnelIds = (sections: IAhuBoardSection[]): Set<number> => {
  const ids = new Set<number>();
  sections.forEach((section) => {
    if (section.sitting_on_id != null) ids.add(section.tunnel_id);
  });
  return ids;
};

const ConfigureTunnel = ({ tunnels, sections }: ConfigureTunnelProps) => {
  const dispatch = useAppDispatch();
  const [patchTunnel, { isLoading: airflowPending }] = usePatchAhuTunnelMutation();
  // `is_hybrid` gates the x_offset half of the airflow flip (legacy read it off
  // `fn['current.ahu']`); every tunnel here belongs to the same AHU.
  const { data: ahuUnit } = useGetAhuUnitQuery(tunnels[0]?.ahu_id ?? skipToken);
  const [moveSection] = useMoveAhuSectionMutation();
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleStart, setVisibleStart] = useState(0);
  const [drag, setDrag] = useState<DragState | null>(null);
  /**
   * The dropped order, held in state so the rows keep it until the server has
   * it — see `orderedCellTunnels`. Null when nothing is outstanding.
   */
  const [pendingDisplayOrder, setPendingDisplayOrder] = useState<number[] | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const geometry = useRef<DragGeometry | null>(null);
  const dragResult = useRef<{ fromIndex: number; toIndex: number } | null>(null);
  /** Unbinds the in-flight gesture's document listeners; null when idle. */
  const detach = useRef<(() => void) | null>(null);
  /** A press that became a drag must not also fire the row's select-on-click. */
  const swallowClick = useRef(false);
  // Belt-and-braces against a double-click landing before `isLoading` renders.
  const airflowInFlight = useRef(false);
  /**
   * The debounced reorder: see `commitTunnelOrder`. `cellKey` is the
   * `row:level` it belongs to — a drop in another tab sends this one first
   * rather than overwriting it (a drag in the tab you just left used to be
   * thrown away).
   */
  const pendingOrder = useRef<
    { ahuId: number; cellKey: string; tunnelIds: number[]; movedType: string } | null
  >(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Latest `flushReorder`, so a scheduled timer never calls a stale closure. */
  const flushRef = useRef<() => void>(() => undefined);

  /**
   * The unit's own `is_hybrid` FLAG decides which window this is — NOT
   * `isHybridAhu`, which also calls a unit hybrid when its tunnels are
   * ADDRESSED like one (product decision, 2026-10-21).
   *
   * Units in that state are mostly standard AHUs the old Add Section flow
   * mis-addressed (`hybrid_edit_protect._hybrid_unit` documents them, AHU
   * 6732 among them), and every other part of the board treats them as
   * standard: no hybrid badge, per-tunnel tabs in the AHU tab, the Data Bar's
   * standard X-Offset rules, and their Standard-skill owners may still edit
   * them. Keying this window off the shape took the per-tunnel tabs, the
   * section drag and Airflow Direction away from exactly those users.
   *
   * `validationUnit` below keeps `isHybridAhu`, as it did before this window
   * changed: that one is about which validation REQUEST shape the server
   * accepts, which does follow the addressing.
   */
  const isHybrid = !!ahuUnit?.is_hybrid;

  /**
   * row:level:position order, the same `compareTunnelAddress` the canvas pill
   * strip (TunnelIndexes) and the Data Bar tunnel list read in. The pills sat
   * in list order, which on a hybrid AHU interleaves the levels — it only
   * became visible once the pills started printing distinct addresses. Every
   * read below (the tabs, the rows, the airflow toggles) is derived from this
   * one array so they stay in step.
   */
  const orderedTunnels = useMemo(() => [...tunnels].sort(compareTunnelAddress), [tunnels]);

  /**
   * The tabs. A hybrid AHU groups the tunnels by `row:level`; since
   * `orderedTunnels` is already in row:level:position order, grouping in
   * encounter order gives the tabs in address order and each tab's tunnels in
   * position order.
   */
  const tabs: TunnelTab[] = useMemo(() => {
    if (!isHybrid) {
      return orderedTunnels.map((tunnel) => ({
        key: String(tunnel.id),
        label: String(tunnel.index ?? ''),
        tunnels: [tunnel],
      }));
    }
    const cells = new Map<string, IAhuBoardTunnel[]>();
    orderedTunnels.forEach((tunnel) => {
      const key = formatRowLevel(tunnel);
      const cell = cells.get(key);
      if (cell) cell.push(tunnel);
      else cells.set(key, [tunnel]);
    });
    return [...cells].map(([key, cellTunnels]) => ({ key, label: key, tunnels: cellTunnels }));
  }, [orderedTunnels, isHybrid]);

  // Hybrid units revalidate in one request after a reorder (see validateAll3D);
  // standard units keep the per-tunnel fan-out.
  const validationUnit = useMemo(() => {
    const ahuId = tunnels[0]?.ahu_id;
    if (ahuId == null) return undefined;
    return { ahuId, isHybrid: isHybridAhu(ahuUnit, tunnels) };
  }, [tunnels, ahuUnit]);

  // Follow external selection (§8.2) to the tab its tunnel is in — the tunnel
  // itself on a standard AHU, the `row:level` cell on a hybrid one.
  useEffect(() => {
    if (currentSectionId == null) return;
    const section = sections.find((s) => s.id === currentSectionId);
    if (!section) return;
    const idx = tabs.findIndex((tab) => tab.tunnels.some((t) => t.id === section.tunnel_id));
    if (idx !== -1) setActiveIndex(idx);
  }, [currentSectionId, sections, tabs]);

  // ensureActiveTunnelIndexVisible (tunnels.section.js) — re-centers the
  // MAX_VISIBLE_TUNNEL_INDEXES-wide tab window on the active tab whenever
  // it changes, so the active tab is never scrolled out of view regardless
  // of how it became active (tab click, canvas/list selection, prev/next
  // nav, section create/delete).
  useEffect(() => {
    setVisibleStart(
      getVisibleTunnelIndexes(activeIndex, tabs.length, MAX_VISIBLE_TUNNEL_INDEXES).start
    );
  }, [activeIndex, tabs.length]);

  const activeTab = tabs[Math.min(activeIndex, Math.max(0, tabs.length - 1))];

  const sectionsOf = (tunnelId: number) =>
    sections
      .filter((s) => s.tunnel_id === tunnelId)
      .sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0));

  /**
   * The cell's tunnels in the order to SHOW them: the order the user dropped
   * them in while that is still on its way to the server, otherwise the
   * server's own `position`.
   *
   * The dropped order cannot be held in the tunnel-list cache alone. The
   * board re-reads that list for its own reasons — a syncdata echo, a
   * validation pass, another panel — and any such read replaces the cache and
   * takes the optimistic patch with it, so the rows snapped back to the old
   * order a second after the drop and every drag after that was measured
   * against a list the user was no longer looking at. Seen on AHU 3477
   * (reported 2026-10-21): a `GET ahu/tunnels/?ahu_id=` five seconds before
   * the reorder POST, and reorders that asked the server for the order it
   * already had.
   */
  const orderedCellTunnels = useMemo(() => {
    const cellTunnels = activeTab?.tunnels ?? [];
    if (!pendingDisplayOrder) return cellTunnels;
    const byId = new Map(cellTunnels.map((t) => [t.id, t]));
    if (
      pendingDisplayOrder.length !== cellTunnels.length ||
      !pendingDisplayOrder.every((id) => byId.has(id))
    ) {
      // The pending order is for another cell, or the cell has changed under
      // it (a section added or deleted) — the server's order is the truth.
      return cellTunnels;
    }
    return pendingDisplayOrder.map((id) => byId.get(id) as IAhuBoardTunnel);
  }, [activeTab, pendingDisplayOrder]);

  /** The rows: this cell's tunnels on a hybrid AHU, the tunnel's sections otherwise. */
  const rows: ListRow[] = useMemo(() => {
    if (!activeTab) return [];
    if (!isHybrid) {
      return sectionsOf(activeTab.tunnels[0].id).map((section) => ({
        id: section.id,
        tunnelId: section.tunnel_id,
        label: sectionLabel(section),
        length: toNumber(section.length),
        sectionId: section.id,
        type: section.type,
      }));
    }
    return orderedCellTunnels.map((tunnel) => {
      const tunnelSections = sectionsOf(tunnel.id);
      return {
        id: tunnel.id,
        tunnelId: tunnel.id,
        label: tunnelSections.map(sectionLabel).join(' + '),
        length: tunnelSections.reduce((total, s) => total + toNumber(s.length), 0),
        sectionId: tunnelSections[0]?.id ?? null,
        type: tunnelSections[0]?.type,
      };
    });
  }, [activeTab, orderedCellTunnels, sections, isHybrid]);

  /** A cell holding the second half of a two-tunnel component cannot be reordered. */
  const dualSecondaryIds = useMemo(() => dualSecondaryTunnelIds(sections), [sections]);
  const cellHasDualComponent =
    isHybrid && !!activeTab && activeTab.tunnels.some((t) => dualSecondaryIds.has(t.id));
  const reorderable = rows.length > 1 && !cellHasDualComponent;

  /**
   * `model/tunnel.js:203-230` gated the toggle by putting `.non-clickable` on
   * every `.btn-tunnel-airflow-direction` for the life of the PATCH, so a
   * second click could not queue another save. The canvas arrow keeps that
   * (AhuCanvas.tsx `toggleAirflowDirection`); the toolbar's own button had
   * lost it, so repeated clicks fired one PATCH each. Re-enable on settle —
   * success *and* failure (legacy leaves it stuck on error).
   *
   * The body itself comes from the shared `airflowFlipPatch`, which carries
   * the re-based `x_offset` on a hybrid AHU — legacy kept that in the one
   * model method all three airflow affordances called, so all three keep it.
   * (A hybrid unit no longer renders this toggle; the canvas arrow and the
   * Data Bar are the two that are left.)
   */
  const toggleAirflow = (tunnel: IAhuBoardTunnel) => {
    if (airflowInFlight.current) return;
    airflowInFlight.current = true;
    patchTunnel({
      id: tunnel.id,
      ahuId: tunnel.ahu_id,
      data: airflowFlipPatch(tunnel, !!ahuUnit?.is_hybrid),
    })
      .unwrap()
      .then(() => {
        // Hybrid: same re-read the pill and the canvas arrow take — the flip
        // restates the unit's lug placement server-side
        // (refetchHybridLugPlacements).
        void dispatch(refetchHybridLugPlacements(tunnel.ahu_id, { hideWhilePending: true }));
        ahuBoardBus.emit('change:tunnel:dimensions', {
          tunnel_id: tunnel.id,
          render: true,
          accessories_fetch: false,
        });
        void dispatch(validateTunnel(tunnel.id));
      })
      .catch(() => undefined)
      .finally(() => {
        airflowInFlight.current = false;
      });
  };

  /** Standard AHU: the dropped row is a section, moved inside its own tunnel. */
  const commitSectionMove = (fromIndex: number, toIndex: number, ordered: number[]) => {
    const tunnelId = activeTab?.tunnels[0]?.id;
    const sourceId = rows[fromIndex]?.id;
    if (tunnelId == null || sourceId == null) return;
    // `next_to` is an *order_rate*, not an index, and the API reads it against
    // the pre-move numbering (section_viewset.move): a downward move lands on
    // `next_to`, an upward one on `next_to + 1`. So the value is the current
    // order_rate of whichever row ends up directly above — `toIndex + 1` going
    // down, `toIndex` going up (0 = upstream edge). This is the same pair of
    // cases legacy computed from the swapped-past row (tunnels.section.js
    // `reorder_sections`: `order_rate < changed ? changed : changed - 1`).
    const nextTo = toIndex > fromIndex ? toIndex + 1 : toIndex;
    const movedType = analyticsComponentName(rows[fromIndex]?.type);
    moveSection({ id: sourceId, next_to: nextTo })
      .unwrap()
      .then(() => {
        track('section_moved', { section_type: movedType, hybrid: false });
        // Silent renumber from DOM order → collection.sort() (§8.2).
        dispatch(
          ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnelId, (cached) => {
            cached.forEach((row) => {
              const idx = ordered.indexOf(row.id);
              if (idx !== -1) row.order_rate = idx + 1;
            });
            cached.sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0));
          })
        );
        ahuBoardBus.emit('sections:reordered', tunnelId);
        ahuBoardBus.emit('redraw:canvas');
        void dispatch(validateAll3D(tunnels.map((t) => t.id), validationUnit));
      })
      .catch(() => undefined);
  };

  const clearDebounce = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = null;
  };

  const scheduleFlush = () => {
    clearDebounce();
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      flushRef.current();
    }, REORDER_DEBOUNCE_MS);
  };

  /**
   * Send the order the rows are in now — one request, however many drops
   * produced it.
   *
   * A reorder of the same CHAIN still out is CANCELLED rather than waited
   * for (requested 2026-10-21): only the latest order for a chain matters,
   * and queueing behind the server's solve made the board feel stuck.
   * `reorderHybridTunnels` does the aborting, per chain, and tells the
   * superseded call to keep its hands off the rows; it also re-checks the
   * stored order afterwards, since an aborted request is only cancelled in
   * the browser and the server may still finish it.
   */
  const flushReorder = () => {
    const pending = pendingOrder.current;
    if (!pending) return;
    pendingOrder.current = null;
    clearDebounce();
    const { ahuId, tunnelIds, movedType } = pending;
    void dispatch(reorderHybridTunnels(ahuId, tunnelIds)).then((outcome) => {
      // Superseded: a newer drop is already on its way with the order the
      // rows are showing. Leave both the rows and the pending state alone.
      if (outcome.status === 'superseded') return;
      if (outcome.status === 'refused') {
        // Anything the server would not take: the six `ChainReorderError`
        // cases (a chain holding the second half of a two-tunnel component is
        // the only one a user can reach, and those rows are not draggable), a
        // permission refusal, or a 5xx/network failure. The thunk has re-read
        // the list, so dropping the pending order shows what it actually
        // holds.
        console.warn('[ConfigureTunnel] reorder refused:', outcome.message);
        setPendingDisplayOrder(null);
        return;
      }
      // After the server confirms, as CLAUDE.md's PostHog rules require —
      // not on the drop, which would also count drops the debounce folded
      // away and orders the server refused.
      track('section_moved', { section_type: movedType, hybrid: true });
      // Same two the section move fires: the section lists are unchanged, but
      // everything downstream of them reads the new tunnel order.
      ahuBoardBus.emit('sections:reordered', tunnelIds[0]);
      void dispatch(validateAll3D(tunnels.map((t) => t.id), validationUnit));
      // The re-read has landed, so the tunnel list carries this order itself
      // — unless another drop came in behind this one, which owns the display
      // until its own turn.
      if (!pendingOrder.current) setPendingDisplayOrder(null);
    });
  };
  flushRef.current = flushReorder;

  /**
   * Hybrid AHU: the dropped row is a TUNNEL, and the cell's whole new order
   * goes to the server in one request (`reorder_tunnels`), which repacks
   * that chain on X and re-numbers its `position`s. Only that row moves —
   * the rest of the unit is left alone.
   *
   * **The request is not sent on the drop.** It waits for
   * `REORDER_DEBOUNCE_MS` of stillness and then goes once, carrying the order
   * the rows ended up in (requested 2026-10-21): shuffling four rows into
   * place is one request and one solve, not four, and the orders the user
   * passed through on the way are never written at all. Picking a row up
   * again restarts the clock (`onRowPointerDown`).
   *
   * The rows themselves render from `pendingDisplayOrder` until the server
   * has the order (see `orderedCellTunnels`). `position` is patched into the
   * tunnel-list cache as well, for everything ELSE that draws the unit — the
   * canvas above all — but that patch is best-effort: the board's own re-read
   * of the list wipes it, which is exactly why the rows do not depend on it.
   *
   * ONE CHAIN AT A TIME. A drop in another tab sends the order waiting for
   * the previous one first: the two are different writes, and overwriting
   * the ref lost the earlier drag silently — it showed until the next
   * tunnel-list read and then went back.
   */
  const commitTunnelOrder = (ordered: number[], movedType: string) => {
    const ahuId = tunnels[0]?.ahu_id;
    const cellKey = activeTab?.key;
    if (ahuId == null || cellKey == null) return;
    const waiting = pendingOrder.current;
    if (waiting && waiting.cellKey !== cellKey) flushReorder();
    dispatch(
      ahuBoardApi.util.updateQueryData('getAhuTunnelList', ahuId, (cached) => {
        cached.forEach((tunnel) => {
          const idx = ordered.indexOf(tunnel.id);
          if (idx !== -1) tunnel.position = idx + 1;
        });
      })
    );
    pendingOrder.current = { ahuId, cellKey, tunnelIds: ordered, movedType };
    setPendingDisplayOrder(ordered);
    scheduleFlush();
  };

  const commitMove = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || !activeTab) return;
    const ordered = rows.map((row) => row.id);
    const sourceId = ordered[fromIndex];
    if (sourceId == null || ordered[toIndex] == null) return;
    ordered.splice(fromIndex, 1);
    ordered.splice(toIndex, 0, sourceId);
    if (isHybrid) commitTunnelOrder(ordered, analyticsComponentName(rows[fromIndex]?.type));
    else commitSectionMove(fromIndex, toIndex, ordered);
  };

  /**
   * Where the row would land, from its current top: the gap in the
   * source-removed list whose top is nearest the floating row's own top. Picked
   * by distance rather than by centres crossing so that the slot is always
   * drawn exactly where the row is being held — a crossing test is a hair's
   * breadth from the wrong answer on the boundary, and the rows are not quite
   * uniform (`li:last-child` drops its 1px rule).
   *
   * Measured against the geometry captured on press: the live DOM moves under
   * us as the slot opens, so reading it back would feed the result into its own
   * input.
   */
  const landingIndex = (g: DragGeometry, top: number) => {
    const others = g.rows.filter((_, i) => i !== g.fromIndex);
    let gapTop = g.rows[0].top;
    let best = 0;
    let bestDistance = Infinity;
    for (let k = 0; k <= others.length; k += 1) {
      const distance = Math.abs(gapTop - top);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = k;
      }
      if (k < others.length) gapTop += others[k].height;
    }
    return best;
  };

  const onRowPointerDown = (e: React.PointerEvent<HTMLLIElement>, fromIndex: number) => {
    const list = listRef.current;
    swallowClick.current = false;
    if (e.button !== 0 || fromIndex < 0 || !list || !reorderable || geometry.current) {
      return;
    }

    const domRows = Array.from(list.querySelectorAll<HTMLLIElement>('li.drag')).map((el) => ({
      top: el.offsetTop,
      height: el.offsetHeight,
    }));
    if (domRows.length !== rows.length) return;

    geometry.current = {
      rows: domRows,
      fromIndex,
      startY: e.clientY,
      id: rows[fromIndex].id,
      viewport: list.clientHeight,
    };
    dragResult.current = null;
    let started = false;

    const onMove = (ev: PointerEvent) => {
      const g = geometry.current;
      if (!g || !listRef.current) return;
      const delta = ev.clientY - g.startY;
      if (!started) {
        if (Math.abs(delta) < DRAG_THRESHOLD_PX) return;
        started = true;
        swallowClick.current = true;
        // Picking a row up restarts the debounce: the rows are moving again,
        // so an order pending from the last drop is not final after all. The
        // gesture's own end re-arms it — `finish` below for a drag that
        // commits nothing, `commitTunnelOrder` for one that does.
        clearDebounce();
      }

      const ul = listRef.current;
      const box = ul.getBoundingClientRect();
      // Legacy stepped the parent's scrollTop rather than running a timer, so
      // the list only creeps while the pointer keeps moving. Same here.
      if (ev.clientY < box.top + EDGE_SCROLL_ZONE_PX) ul.scrollTop -= EDGE_SCROLL_STEP_PX;
      else if (ev.clientY > box.bottom - EDGE_SCROLL_ZONE_PX) ul.scrollTop += EDGE_SCROLL_STEP_PX;

      // Y only, and never past the list's visible box — the two things the
      // native drag ghost would not honour.
      const source = g.rows[g.fromIndex];
      const min = ul.scrollTop;
      const max = Math.max(min, min + g.viewport - source.height);
      const top = Math.min(Math.max(source.top + delta, min), max);

      const toIndex = landingIndex(g, top);
      dragResult.current = { fromIndex: g.fromIndex, toIndex };
      setDrag({ id: g.id, fromIndex: g.fromIndex, toIndex, top, height: source.height });
    };

    const finish = (commit: boolean) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
      detach.current = null;
      const result = dragResult.current;
      geometry.current = null;
      dragResult.current = null;
      setDrag(null);
      if (commit && result) commitMove(result.fromIndex, result.toIndex);
      // A gesture that committed nothing (cancelled, or dropped back where it
      // started) still cleared the debounce on pick-up, so re-arm it — an
      // order from an earlier drop is still waiting to go.
      else if (pendingOrder.current && !debounceTimer.current) scheduleFlush();
    };
    const onUp = () => finish(true);
    const onCancel = () => finish(false);

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
    detach.current = () => finish(false);
  };

  // Leaving the board mid-gesture would otherwise leave the document
  // listeners bound, and a drop still inside its debounce window would never
  // be sent — so it goes now instead. (This window is hidden rather than
  // unmounted by the toolbar, so this fires on the board going away, not on
  // the window closing; `windowOpen` below is the close.)
  useEffect(
    () => () => {
      detach.current?.();
      clearDebounce();
      flushRef.current();
    },
    []
  );

  // Closing the window is the user walking away from the list: send the
  // pending order straight away rather than leave it on a timer behind a
  // panel nobody can see.
  const windowOpen = useAppSelector((s) => s.ahuBoard.toolbar.openWindow) === 'configure_tunnel';
  useEffect(() => {
    if (windowOpen || !pendingOrder.current) return;
    clearDebounce();
    flushRef.current();
  }, [windowOpen]);

  /**
   * "reordering sections..." on the canvas strip, for a reorder that is still
   * on its way while the window is CLOSED (requested 2026-10-21). With the
   * window open the panel prints its own note instead, so exactly one of the
   * two is ever on screen — and opening or closing the window mid-request
   * swaps them, since this runs on `windowOpen` as well as on the request.
   *
   * `classList` rather than React: `.messages_wrapper` is markup in
   * BoardContent that every other note in the strip is toggled on the same
   * way (boardActions `noteAddingSection`, SectionToolbarChrome's delete).
   */
  const reorderPending = pendingDisplayOrder != null;
  useEffect(() => {
    const note = document.querySelector('.reordering-sections-note');
    note?.classList.toggle('active', reorderPending && !windowOpen);
    return () => note?.classList.remove('active');
  }, [reorderPending, windowOpen]);

  // Leaving a tab with a drop still waiting sends it now. The debounce is
  // about a user still shuffling THIS row; once they are looking at another
  // one they are not, and a pending order must not sit behind a list nobody
  // is reading (the same reasoning as the window-close flush above).
  useEffect(() => {
    const waiting = pendingOrder.current;
    if (!waiting || waiting.cellKey === activeTab?.key) return;
    clearDebounce();
    flushRef.current();
  }, [activeTab?.key]);

  const dragged = drag ? rows.find((row) => row.id === drag.id) ?? null : null;
  const staticRows = dragged ? rows.filter((row) => row.id !== drag?.id) : rows;

  const renderRow = (row: ListRow, floating: boolean) => (
    <li
      key={row.id}
      className={`clearfix drag${floating ? ' is-dragging' : ''}`}
      id="section-item"
      data-id={row.sectionId ?? row.id}
      data-name={row.tunnelId}
      // Offset by the slot's pad so a snapped row sits centred in its band.
      style={floating && drag ? { top: drag.top + DROP_SLOT_PAD_PX } : undefined}
      onPointerDown={
        floating
          ? undefined
          : (e) => onRowPointerDown(e, rows.findIndex((r) => r.id === row.id))
      }
      onMouseEnter={() =>
        row.sectionId != null &&
        ahuBoardBus.emit('hover:section', {
          section: { id: row.sectionId },
          color: 0x9bb3d7,
        })
      }
      onMouseLeave={() => ahuBoardBus.emit('hover:section', { section: null })}
      onClick={() => {
        if (swallowClick.current) {
          swallowClick.current = false;
          return;
        }
        if (row.sectionId == null) return;
        ahuBoardBus.emit('force:select:section', row.sectionId);
        dispatch(selectSection({ sectionId: row.sectionId }));
        ahuBoardBus.emit('select:section', { section: { id: row.sectionId } });
      }}
    >
      <label>
        <span className="section-name">{row.label}</span>
        <span className="number_output" data-format="decimal">
          {formatDecimal(row.length)}
        </span>
        <i className="icon-two-lines" />
      </label>
    </li>
  );

  const dropSlot = drag ? (
    <li
      className="section-drop-slot"
      style={{ height: drag.height + DROP_SLOT_PAD_PX * 2 }}
      aria-hidden
    />
  ) : null;

  const tabsTotal = tabs.length;
  const tabsMaxStart = Math.max(0, tabsTotal - MAX_VISIBLE_TUNNEL_INDEXES);
  const tabsWindowStart = Math.max(0, Math.min(visibleStart, tabsMaxStart));
  const tabsWindowEnd = Math.min(tabsTotal, tabsWindowStart + MAX_VISIBLE_TUNNEL_INDEXES);
  const tabsShowNav = tabsTotal > MAX_VISIBLE_TUNNEL_INDEXES;
  const tabsCenterSlot = Math.floor(MAX_VISIBLE_TUNNEL_INDEXES / 2);
  // shiftVisibleTunnelIndexes (tunnels.section.js) — shifts the window
  // without touching which tab is active.
  const shiftTabsWindow = (direction: number) =>
    setVisibleStart(Math.max(0, Math.min(tabsMaxStart, tabsWindowStart + direction)));

  return (
    <>
      <div className="tunnels-header">
        <span className="pl-24">Tunnel </span>
        <div className="tunnels-indexes">
          <div className="tunnels-name-list">
            {tabs.slice(tabsWindowStart, tabsWindowEnd).map((tab, i) => {
              const idx = tabsWindowStart + i;
              const isActive = idx === activeIndex;
              const isCentered =
                tabsShowNav && isActive && idx - tabsWindowStart === tabsCenterSlot;
              return (
                <span
                  key={tab.key}
                  data-index={idx}
                  data-id={tab.tunnels[0]?.id}
                  className={`tunnels-name tunnels-name-index${isActive ? ' active ' : ''}${
                    isCentered ? ' tunnel-index-centered' : ''
                  }`}
                  onClick={() => setActiveIndex(idx)}
                >
                  {tab.label}
                </span>
              );
            })}
          </div>
          <div className={`tunnels-indexes-nav${tabsShowNav ? '' : ' hide'}`}>
            <label
              className={`icon-chevron-left tunnels-indexes-arrow tunnels-indexes-arrow-left ka-has-tooltip${
                tabsWindowStart <= 0 ? ' disabled' : ''
              }`}
              {...{ tooltip: 'Show Previous' }}
              onClick={() => tabsWindowStart > 0 && shiftTabsWindow(-1)}
            />
            <label
              className={`icon-chevron-right tunnels-indexes-arrow tunnels-indexes-arrow-right ka-has-tooltip${
                tabsWindowEnd >= tabsTotal ? ' disabled' : ''
              }`}
              {...{ tooltip: 'Show Next' }}
              onClick={() => tabsWindowEnd < tabsTotal && shiftTabsWindow(1)}
            />
          </div>
        </div>
        {/* Airflow Direction — standard AHUs only (see the header comment). */}
        {!isHybrid &&
          orderedTunnels.map((t, idx) => (
            <div
              key={t.id}
              className={`tunnel-list${idx === activeIndex ? ' active ' : ''}`}
              data-name={t.id}
            >
              <div className="directions-wrapper">
                <div className="hide">Section insertion direction</div>
                <div>
                  <span className="airflow_direction" id={String(t.id)}>
                    Airflow Direction
                  </span>
                  <label
                    id={String(t.id)}
                    className={`icon-airflow-direction-icon btn-tunnel-airflow-direction ka-has-tooltip left-to-right left-to-right-${t.id}${
                      t.airflow_direction_id === 2 ? ' hide' : ''
                    }${airflowPending ? ' non-clickable' : ''}`}
                    aria-disabled={airflowPending}
                    {...{ tooltip: 'Reverse Direction' }}
                    onClick={() => toggleAirflow(t)}
                  />
                  <label
                    id={String(t.id)}
                    className={`icon-airflow-direction-icon btn-tunnel-airflow-direction ka-has-tooltip right-to-left right-to-left-${t.id}${
                      t.airflow_direction_id === 1 ? ' hide' : ''
                    }${airflowPending ? ' non-clickable' : ''}`}
                    aria-disabled={airflowPending}
                    {...{ tooltip: 'Reverse Direction' }}
                    onClick={() => toggleAirflow(t)}
                  />
                </div>
              </div>
            </div>
          ))}
      </div>
      <span className="sections-header">
        <span> Section : Tag</span>
        <span>Length</span>
      </span>
      <ul ref={listRef} className={`sections-list-ul${drag ? ' is-reordering' : ''}`}>
        {/* First, not last: the floating row is out of flow so its position in
            the DOM is free, and keeping it off `li:last-child` stops it from
            dropping its 1px rule and changing height mid-drag. */}
        {dragged && renderRow(dragged, true)}
        {staticRows.map((row, i) => (
          <Fragment key={row.id}>
            {drag?.toIndex === i && dropSlot}
            {renderRow(row, false)}
          </Fragment>
        ))}
        {drag && drag.toIndex >= staticRows.length && dropSlot}
      </ul>
      {/* The only note the panel carries: a reorder is on its way to the
          server (requested 2026-10-21). Nothing is said about a cell that
          will not reorder — those rows simply do not pick up — and a refusal
          is logged to the console, the rows falling back to the stored order.
          With the window CLOSED this moves to the canvas strip; see the
          effect above.

          It renders here but PAINTS beside the window's "Configure Tunnel"
          title, which is markup this component does not own (CanvasToolbar):
          the rule positions it against `.toolbar-item`, the window itself. */}
      {reorderPending && windowOpen && (
        <div className="sections-reorder-note">Reordering sections...</div>
      )}
    </>
  );
};

export default ConfigureTunnel;
